<template>
  <div v-if="modelValue && modelValue.items" class="flex items-stretch gap-5">
    <condition-connector
      v-model="modelValue.logic"
      :item-count="modelValue.items.length"
      :allow-add-new-group="level < 4"
      :disabled="disabled"
      @add-item="addItem"
      @add-group="addGroup"
    />

    <div class="flex min-w-0 flex-1 flex-col gap-2.5">
      <!-- The short line links each item to the AND/OR bar. -->
      <div
        v-for="(item, key) in modelValue.items"
        :key="'level-' + level + '-item-' + key"
        class="relative before:absolute before:top-1/2 before:-left-5 before:h-px before:w-5 before:bg-border"
      >
        <condition-group
          v-if="!!item.logic"
          v-model="modelValue.items[key]"
          :level="level + 1"
          :disabled="disabled"
          :action="action"
          :platforms="platforms"
          :disable-post-metrics="disablePostMetrics"
          :ad-account="adAccount"
          @remove="() => removeItem(key)"
        />
        <condition-row
          v-else
          v-model="modelValue.items[key]"
          :action="action"
          :platforms="platforms"
          :ad-account="adAccount"
          :removable="level !== 1 || modelValue.items.length !== 1"
          :disable-post-metrics="disablePostMetrics"
          :disabled="disabled"
          :subfields-map="allSubfields"
          :loading-subfields="loadingSubfields"
          @remove="() => removeItem(key)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import AdAccount from '@/entities/AdAccount';
import { ConditionValidatable, ConditionValidatableProvider } from '@/mixins/RuleForm/validatable.js';
import ConditionConnector from '@/ui/components/RuleForm/Condition/ConditionConnector.vue';
import ConditionRow from '@/ui/components/RuleForm/Condition/ConditionRow.vue';

export default {
  name: 'condition-group',
  components: {
    ConditionConnector,
    ConditionRow,
  },
  mixins: [ ConditionValidatableProvider, ConditionValidatable ],
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    action: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
    platforms: {
      type: Array,
      required: false,
      default: () => [ 'facebook', 'instagram' ],
    },
    level: {
      type: Number,
      required: false,
      default: () => 1,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    disablePostMetrics: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    adAccount: {
      type: [ AdAccount, null ],
      required: false,
      default: () => null,
    },
  },
  computed: {
    allSubfields() {
      return this.adAccount
        ? (this.$store.getters['dynamicSubfields/allSubfields'](this.adAccount.id) || {})
        : {};
    },
    loadingSubfields() {
      return this.adAccount
        ? this.$store.getters['dynamicSubfields/isLoading'](this.adAccount.id)
        : false;
    },
  },
  watch: {
    adAccount: {
      immediate: true,
      handler() {
        if (this.adAccount) {
          this.$store.dispatch('dynamicSubfields/fetch', { adAccountId: this.adAccount.id })
            .catch(err => this.error(err));
        }
      },
    },
  },
  created() {
    if (!this.modelValue || !this.modelValue.items) {
      this.$emit('update:modelValue', JSON.parse(JSON.stringify(INITIAL_VALUE)));
    }
  },
  methods: {
    addItem() {
      const newValue = JSON.parse(JSON.stringify(this.modelValue));
      newValue.items.push(JSON.parse(JSON.stringify(EMPTY_ITEM)));

      this.$emit('update:modelValue', newValue);
    },
    addGroup() {
      const newValue = JSON.parse(JSON.stringify(this.modelValue));
      newValue.items.push(JSON.parse(JSON.stringify(EMPTY_GROUP)));

      this.$emit('update:modelValue', newValue);
    },
    removeItem(key) {
      if (this.modelValue.items.length === 1) {
        if (this.level > 1) {
          this.$emit('remove');
        }
        else {
          this.$emit('update:modelValue', JSON.parse(JSON.stringify(INITIAL_VALUE)));
        }
      }
      else {
        const newValue = JSON.parse(JSON.stringify(this.modelValue));
        newValue.items.splice(key, 1);

        this.$emit('update:modelValue', newValue);
      }
    },
    validate() {
      return this.validateRegistered();
    },
  },
};

const EMPTY_ITEM = {
  field: '',
  subfield: null,
  date_period: null,
  comparison: '',
  value: '',
  settings: {
    dynamic_operator_direction: null,
    dynamic_operator_area: null,
  },
};

const EMPTY_GROUP = {
  logic: 'AND',
  items: [ EMPTY_ITEM, EMPTY_ITEM ],
};

const INITIAL_VALUE = {
  logic: 'AND',
  items: [ EMPTY_ITEM ],
};
</script>
